import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox,
    Typography,
    Chip,
    Divider,
    Alert,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Settings } from '@mui/icons-material';
import { ColumnPreferenceManager, ColumnPreference } from 'utils/ColumnPreferenceManager';
import { Logger } from 'utils/Logger';

interface ColumnSelectorProps {
    dataSetName: string;
    allSchemaColumns: string[];
    allowedOptionalColumns?: string[];
    onColumnPreferencesChange: (preferences: ColumnPreference) => void;
    currentPreferences: ColumnPreference;
    mandatoryColumns?: string[];
}

export const ColumnSelector: React.FC<ColumnSelectorProps> = ({
    dataSetName,
    allSchemaColumns,
    allowedOptionalColumns,
    onColumnPreferencesChange,
    currentPreferences,
    mandatoryColumns = ['firstName', 'lastName', 'primaryMobile', 'primaryEmail'],
}) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedOptionalColumns, setSelectedOptionalColumns] = useState<string[]>([]);
    const [availableOptionalColumns, setAvailableOptionalColumns] = useState<string[]>([]);

    // Mobile responsive state
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Initialize available optional columns only once when component mounts
    useEffect(() => {
        let optionalColumns = allSchemaColumns.filter(col => 
            !mandatoryColumns.includes(col) && 
            !['_id', 'createdAt', 'updatedAt', '__v'].includes(col)
        );
        if (allowedOptionalColumns && allowedOptionalColumns.length > 0) {
            optionalColumns = optionalColumns.filter(col => allowedOptionalColumns.includes(col));
        }
        setAvailableOptionalColumns(optionalColumns);
    }, []); // Empty dependency array - only run once

    const handleOpenDialog = () => {
        // Sync with current preferences when opening dialog
        setSelectedOptionalColumns(currentPreferences.optionalColumns || []);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleSavePreferences = () => {
        try {
            const newPreferences: ColumnPreference = {
                mandatoryColumns,
                optionalColumns: selectedOptionalColumns,
                allColumns: [...mandatoryColumns, ...selectedOptionalColumns]
            };
            ColumnPreferenceManager.saveColumnPreferences(dataSetName, newPreferences);
            onColumnPreferencesChange(newPreferences);
            setDialogOpen(false);
            Logger.debug("ColumnSelector", "Preferences updated successfully");
        } catch (error) {
            Logger.error("ColumnSelector", "Error updating preferences: " + error);
        }
    };

    const handleColumnToggle = (columnName: string) => {
        setSelectedOptionalColumns(prev => 
            prev.includes(columnName)
                ? prev.filter(col => col !== columnName)
                : [...prev, columnName]
        );
    };

    return (
        <Box>
            {!isMobile ? (
                // Desktop View - Full Button
                <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<Settings />}
                    onClick={handleOpenDialog}
                    sx={{ 
                        height: '48px',
                        whiteSpace: 'nowrap'
                    }}
                >
                    Column Settings
                </Button>
            ) : (
                // Mobile View - Icon Button
                <IconButton
                    color="secondary"
                    onClick={handleOpenDialog}
                    sx={{
                        border: '1px solid',
                        borderColor: 'secondary.main',
                        color: 'secondary.main',
                        '&:hover': {
                            backgroundColor: 'secondary.main',
                            color: 'secondary.contrastText',
                        },
                        p: 1.5,
                        minWidth: 48,
                        minHeight: 48
                    }}
                    title="Column Settings"
                >
                    <Settings />
                </IconButton>
            )}

            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box>
                        <Typography variant="h6" component="div">Column Display Settings</Typography>
                        <Typography variant="body2" color="textSecondary">
                            Choose which columns to display in the table
                        </Typography>
                    </Box>
                </DialogTitle>
                
                <DialogContent>
                    <Box sx={{ mb: 3, mt: 1.25 }}>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                            Mandatory Columns (Always Displayed)
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {mandatoryColumns.map(column => (
                                <Chip
                                    key={column}
                                    label={column}
                                    color="primary"
                                    variant="filled"
                                    size="small"
                                />
                            ))}
                        </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                            Optional Columns
                        </Typography>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Select which additional columns you want to display in the table
                        </Alert>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {availableOptionalColumns.length > 0 ? (
                                availableOptionalColumns.map(column => (
                                    <FormControlLabel
                                        key={column}
                                        control={
                                            <Checkbox
                                                checked={selectedOptionalColumns.includes(column)}
                                                onChange={() => handleColumnToggle(column)}
                                                color="secondary"
                                            />
                                        }
                                        label={column}
                                    />
                                ))
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    No optional columns available for this dataset.
                                </Typography>
                            )}
                        </Box>
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                            Note: System fields (_id, createdAt, updatedAt, __v) are automatically excluded from the display options.
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Summary
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Total columns to display: {mandatoryColumns.length + selectedOptionalColumns.length}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Selected optional columns: {selectedOptionalColumns.length}
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button 
                        onClick={handleCloseDialog} 
                        variant="outlined" 
                        color="secondary"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSavePreferences} 
                        variant="contained" 
                        color="success"
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}; 